// src/components/MonthlyCalendarModal.js

import React, { useEffect, useMemo, useState } from "react";
import { Star, CaretLeft, CaretRight, X } from "phosphor-react";
import ActivityLightbox from "./ActivityLightbox";
import useStreakDays from "../hooks/useStreakDays";
import { api } from "../api";

const withUserFallback = (a, user) => {
  if (!a) return a;

  const hasUser = a.user && (a.user.id || a.user.name);
  if (hasUser || !user) return a;

  return {
    ...a,
    user: {
      id: user.id,
      name:
        user.name ||
        user.display_name ||
        [user.first_name, user.last_name].filter(Boolean).join(" ") ||
        user.username ||
        `User #${user.id}`,
      profile_pic_url: user.profile_pic_url || user.avatar_url || null,
    },
  };
};

const isRichActivity = (a) => {
  if (!a) return false;

  const hasMedia =
    (Array.isArray(a.media) && a.media.length > 0) ||
    (Array.isArray(a.media_files) && a.media_files.length > 0) ||
    (Array.isArray(a.images) && a.images.length > 0) ||
    (Array.isArray(a.videos) && a.videos.length > 0) ||
    !!a.media_url ||
    !!a.image_url ||
    !!a.video_url;

  const hasTags =
    (Array.isArray(a.tags) && a.tags.length > 0) ||
    (typeof a.tags === "string" && a.tags.trim().length > 0);

  return hasMedia || hasTags || !!a.description;
};

const normalizeActivity = (a) => {
  if (!a) return a;

  let tags = a.tags;
  if (typeof tags === "string") {
    tags = tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  if (!Array.isArray(tags)) tags = [];

  const media = [];

  if (Array.isArray(a.media)) {
    a.media.forEach((m) => {
      if (!m) return;

      if (typeof m === "string") {
        media.push({ type: inferType(m), url: m });
      } else if (m.url) {
        media.push({ type: inferType(m.url, m.type), url: m.url });
      }
    });
  }

  if (Array.isArray(a.media_files)) {
    a.media_files.forEach((m) => {
      if (!m) return;

      const url = m.url || m.src || m.path;
      if (url) media.push({ type: inferType(url, m.type), url });
    });
  }

  if (Array.isArray(a.images)) {
    a.images.forEach((url) => url && media.push({ type: "image", url }));
  }

  if (Array.isArray(a.videos)) {
    a.videos.forEach((url) => url && media.push({ type: "video", url }));
  }

  if (a.media_url) {
    media.push({ type: inferType(a.media_url), url: a.media_url });
  }
  if (a.image_url) {
    media.push({ type: "image", url: a.image_url });
  }
  if (a.video_url) {
    media.push({ type: "video", url: a.video_url });
  }

  const seen = new Set();
  const deduped = media.filter((m) => {
    if (!m?.url) return false;
    if (seen.has(m.url)) return false;

    seen.add(m.url);
    return true;
  });

  return { ...a, tags, media: deduped };
};

function inferType(url, fallback) {
  const u = String(url || "").toLowerCase();

  if (u.endsWith(".mp4") || u.endsWith(".webm") || u.endsWith(".mov")) {
    return "video";
  }
  if (u.startsWith("data:video/")) return "video";

  if (
    u.endsWith(".jpg") ||
    u.endsWith(".jpeg") ||
    u.endsWith(".png") ||
    u.endsWith(".webp") ||
    u.endsWith(".gif")
  ) {
    return "image";
  }
  if (u.startsWith("data:image/")) return "image";

  return fallback || "image";
}

export default function MonthlyCalendarModal({
  open,
  onClose,
  activitiesByDay = {},
  currentUser,
  streakDays,
}) {
  const computedStreak = useStreakDays({ streakDays, activitiesByDay });
  const [lb, setLb] = useState({ open: false, activity: null, id: null });

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () =>
      setIsMobile(window.matchMedia("(max-width: 767px)").matches);

    check();
    window.addEventListener("resize", check);

    return () => window.removeEventListener("resize", check);
  }, []);

  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const viewYear = viewDate.getFullYear();
  const viewMonth = viewDate.getMonth();

  const monthShort = viewDate
    .toLocaleString("en-US", { month: "short" })
    .toUpperCase();

  const addMonths = (date, delta) => {
    const d = new Date(date);
    d.setMonth(d.getMonth() + delta);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  };

  const pad2 = (n) => String(n).padStart(2, "0");
  const toYMD = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

  const first = new Date(viewYear, viewMonth, 1);
  const startDow = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells = useMemo(() => {
    const arr = [];
    const total = Math.ceil((startDow + daysInMonth) / 7) * 7;

    for (let i = 0; i < total; i += 1) {
      const dayNum = i - startDow + 1;

      if (dayNum < 1 || dayNum > daysInMonth) {
        arr.push({ label: "", ymd: null, stars: [] });
        continue;
      }

      const ymdKey = toYMD(viewYear, viewMonth, dayNum);

      const stars = (activitiesByDay[ymdKey] || [])
        .slice()
        .sort(
          (a, b) =>
            new Date(a.timestamp || a.created_at) -
            new Date(b.timestamp || b.created_at)
        );

      arr.push({ label: String(dayNum), ymd: ymdKey, stars });
    }

    return arr;
  }, [activitiesByDay, daysInMonth, startDow, viewMonth, viewYear]);

  useEffect(() => {
    if (!lb.open || !lb.id) return;
    if (isRichActivity(lb.activity)) return;

    let cancelled = false;

    (async () => {
      try {
        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token") ||
          null;

        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        const tryGet = async (path) => {
          try {
            const r = await api.get(path, { headers });
            return r?.data || null;
          } catch {
            return null;
          }
        };

        const data =
          (await tryGet(`/activities/${lb.id}`)) ||
          (await tryGet(`/activity/${lb.id}`)) ||
          (await tryGet(`/api/activities/${lb.id}`)) ||
          null;

        if (!cancelled && data?.id) {
          const hydrated = withUserFallback(normalizeActivity(data), currentUser);

          setLb((prev) =>
            prev.id === data.id ? { ...prev, activity: hydrated } : prev
          );
        }
      } catch {
        // non-fatal
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lb.open, lb.id, lb.activity, currentUser]);

  if (!open) return null;

  const monthActiveCount = cells.filter(
    (c) => c.ymd && c.stars?.length > 0
  ).length;

  return (
    <div className="fixed inset-0 z-[150] grid place-items-center bg-black/35 overflow-y-auto p-0 md:p-6">
      <div
        className="
          relative w-[94vw] max-w-4xl overflow-hidden
          rounded-[28px] border border-blue-100/80
          bg-blue-50
          p-4 md:p-5 sm:shadow-[0_8px_0px_rgba(37,99,235,0.96)]
        "
      >
        <button
          onClick={onClose}
          className="
            absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center
            rounded-full bg-white/80 text-blue-500
            transition hover:bg-blue-200
          "
          aria-label="Close"
        >
          <X size={19} weight="bold" />
        </button>

        {/* Title + month navigation */}
        <div className="text-center">
          <div className="mt-1 flex items-center justify-center">
            <button
              type="button"
              onClick={() => setViewDate((d) => addMonths(d, -1))}
              className="grid h-9 w-9 place-items-center rounded-full text-blue-400 transition hover:bg-white"
              aria-label="Previous month"
              title="Previous month"
            >
              <CaretLeft size={25} weight="fill" className="text-blue-400" />
            </button>

            <h2 className="mx-2 text-blue-600 pangolin-font font-black uppercase leading-none tracking-[-0.04em] text-[36px] md:text-[40px]">
              {monthShort}
            </h2>

            <button
              type="button"
              onClick={() => setViewDate((d) => addMonths(d, 1))}
              className="grid h-9 w-9 place-items-center rounded-full text-blue-400 transition hover:bg-white"
              aria-label="Next month"
              title="Next month"
            >
              <CaretRight size={25} weight="fill" className="text-blue-400" />
            </button>
          </div>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 outfit-font gap-1.5 md:gap-2.5 mt-5 mb-1 text-blue-600 font-semibold">
          {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
            <div key={d} className="pl-2 text-sm">
              {d}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-7 gap-1.5 md:gap-2.5">
          {cells.map((c, idx) => {
            const maxStars = isMobile ? 1 : 8;
            const extra = Math.max(0, (c.stars?.length || 0) - maxStars);
            const starsToShow = (c.stars || []).slice(0, maxStars);
            const dense = (c.stars?.length || 0) > (isMobile ? 3 : 4);
            const starSize = isMobile ? 18 : dense ? 14 : 16;

            return (
              <div
                key={idx}
                className="
                  group relative min-h-[74px] md:min-h-[96px]
                  overflow-hidden rounded-[22px]
                  border border-blue-100/80 bg-white/90
                  p-1.5 md:p-2
                  shadow-[0_10px_30px_rgba(37,99,235,0.06)]
                  transition hover:-translate-y-0.5
                  hover:shadow-[0_16px_40px_rgba(37,99,235,0.10)]
                "
              >
                {c.label && (
                  <div className="ml-0.5 text-blue-700 text-sm outfit-font font-semibold">
                    {c.label}
                  </div>
                )}

                {(starsToShow.length > 0 || extra > 0) && (
                  <div
                    className={
                      isMobile
                        ? "mt-0.5 ml-0.5 flex items-center justify-start"
                        : "mt-1 ml-0.5 grid grid-cols-4 auto-rows-max gap-1 pr-1"
                    }
                  >
                    {starsToShow.map((a) => (
                      <button
                        key={a.id}
                        title="View activity"
                        onClick={() =>
                          setLb({
                            open: true,
                            activity: null,
                            id: a.id,
                          })
                        }
                        className="p-0 rounded active:scale-95 transition"
                      >
                        <Star
                          size={starSize}
                          weight="fill"
                          className="text-blue-400"
                        />
                      </button>
                    ))}

                    {extra > 0 && (
                      <span className="text-[10px] leading-none px-1 py-0.5 rounded-full bg-blue-50 text-blue-700 self-center justify-self-start">
                        +{extra}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {monthActiveCount >= 1 && (
          <div className="mt-6 flex items-center justify-center">
            <div className="rounded-full border border-blue-100 bg-white/80 px-3 py-1 text-blue-700 shadow-[0_2px_0px_rgba(37,99,235,0.96)]">
              <span className="font-semibold text-sm md:text-sm">
                {computedStreak || monthActiveCount}-day streak, keep going!
              </span>
            </div>
          </div>
        )}
      </div>

      <ActivityLightbox
        key={lb.id || "none"}
        open={lb.open}
        onClose={() => setLb({ open: false, activity: null, id: null })}
        activity={null}
        activityId={lb.id}
        currentUser={currentUser}
      />
    </div>
  );
}